import { Link } from "react-router-dom";
import styled from "styled-components";
import { card, linkPills, softCard } from "../../style/styled-components/mixins";
import TitleTemplate from "../../components/ui/common/template/TitleTemplate";
import PageTemplate from "../../components/ui/common/template/PageTemplate";
import WorkScopes from "../../components/ui/specialty/WorkScopes";
import DiamondScope from "../../components/ui/specialty/DiamondScope";
import PlatformLink from "../../components/ui/specialty/PlatformLink";
import ContactPoints from "../../components/ui/members/ContactPoints";
import ProcessElement from "../../components/ui/common/ProcessElement";
import UseBreakpoints from "../../components/hooks/ui/UseBreakpoints";
import GlobeImage from "../../resources/images/express.jpg";
import imgSupply from "../../resources/images/a1tcms/06-supply-chain.png";
import icon1 from "../../resources/images/icon/10. 기업진단/1. 관세업무 현황진단.png";
import icon2 from "../../resources/images/icon/7. FTA/4. 원산지요건 확인.png";
import icon3 from "../../resources/images/icon/4. 요건/1. 수출입요건 사전 검토서비스.png";
import icon4 from "../../resources/images/icon/7. FTA/8. 각 협정별 법률 자문_rev.png";
import icon5 from "../../resources/images/icon/7. FTA/6. 원산지검증 조력서비스.png";
import icon6 from "../../resources/images/icon/10. 기업진단/6. FTA 적용 적정성 진단.png";

// 근거: ASCP 시스템 체계 정의서 v1 (ACRIS > GSCM), https://a1tcms.com 확장 모듈 소개
const SupplyChainPage = () => {
  const breakpoints = UseBreakpoints();
  return (
    <PageTemplate
      large
      image={GlobeImage}
      title="글로벌 공급망·수출통제"
      phrase="관세를 넘어 공급망 전체의 통상 리스크를 관리합니다."
    >
      <p>
        미국 품목관세·상호관세, 수출통제, 강제노동 규제, 탄소국경조정제도(CBAM)처럼 통상 규제가 관세 밖으로 넓어지고 있습니다.
        에이원은 관세사의 법률 판단에 ASCP의 공급망 분석(GSCM)을 더해, 제품을 이루는 부품(BOM) 단위까지 규제 해당 여부를 점검합니다.
      </p>

      <TitleTemplate
        title="왜 지금 공급망 관리인가"
        noImage
        subHeader="같은 제품이라도 원재료·부품의 출처에 따라 수출 가능 여부와 부담 관세가 달라집니다."
      />
      <Issues breakpoints={breakpoints}>
        <div>
          <b>美 관세 조치</b>
          <p>품목관세(Section 232)·301조 관세 대상은 HS 품목번호와 원산지로 갈립니다. 관세청 한-미 품목번호 연계표로 1차 확인이 필요합니다.</p>
        </div>
        <div>
          <b>수출통제·제재</b>
          <p>美 BIS Entity List·EAR, OFAC 제재, 국내 전략물자 고시 개정에 따라 거래 상대방과 품목을 함께 점검해야 합니다.</p>
        </div>
        <div>
          <b>강제노동·환경 규제</b>
          <p>美 UFLPA는 공급망 내 특정 지역 원재료 미사용 입증을, EU CBAM은 탄소 배출 정보 제출을 요구합니다.</p>
        </div>
      </Issues>

      <TitleTemplate
        title="업무 범위"
        noImage
        subHeader="규제 진단부터 대응 전략, 사후 관리까지 수행합니다."
      />
      <WorkScopes
        contents={[
          { title: "공급망 통상 리스크 진단", icon: icon1 },
          { title: "BOM 단위 원산지 분석", icon: icon2 },
          { title: "수출통제·전략물자 해당 여부 검토", icon: icon3 },
          { title: "美 관세 조치 대상 검토·대응", icon: icon4 },
          { title: "UFLPA·CBAM 대응 자문", icon: icon5 },
          { title: "공급망 재설계 시 FTA 활용 검토", icon: icon6 },
        ]}
      />

      <TitleTemplate
        title="진행 절차"
        noImage
        subHeader="자료는 고객사 내부에서 처리하고, 판단은 관세사가 확정합니다."
      />
      <Process breakpoints={breakpoints}>
        <ProcessElement noLine smallTitle title="01 자료 수집" details={["제품·BOM", "거래처·공급사 목록"]} />
        <ProcessElement noLine smallTitle title="02 자동 스크리닝" details={["Entity List·EAR·OFAC", "Section 301/232·UFLPA"]} />
        <ProcessElement noLine smallTitle title="03 관세사 검토" details={["해당 여부 판단", "법적 근거 정리"]} />
        <ProcessElement noLine smallTitle title="04 대응 전략" details={["대체 조달·원산지 관리", "신고·증빙 체계"]} />
      </Process>

      <TitleTemplate
        title="ASCP 연계"
        noImage
        subHeader="에이원 자체 플랫폼으로 점검 범위와 속도를 높입니다."
      />
      <PlatformLink
        system="ACRIS"
        module="GSCM · 글로벌 공급망"
        text="BIS 수출통제·OFAC 제재·UFLPA 강제노동·CBAM·전략물자를 교차 분석하고, BOM 단위로 자동 스크리닝합니다. 고객사 서버에 설치해 공급망 데이터가 외부로 나가지 않습니다."
        image={imgSupply}
      />

      <TitleTemplate
        title="Strength"
        noImage
        subHeader="통관 실행과 컨설팅을 함께하는 관세법인만의 공급망 관리입니다."
      />
      <DiamondScope
        contents={[
          { title: "법률 판단", text: "관세·대외무역·외국환 법령에 기반한 관세사의 최종 판단" },
          { title: "BOM 분석", text: "완제품이 아닌 부품 단위까지 규제 해당 여부 점검" },
          { title: "통관 연계", text: "점검 결과를 실제 수출입신고·원산지 관리에 바로 반영" },
          { title: "최신 동향", text: "오늘의 브리핑으로 주요 규제 변경 확인" },
        ]}
      />
      <Links>
        <Link to="/boards/briefing">오늘의 브리핑에서 최신 통상 동향 보기 →</Link>
        <Link to="/scm/compliance">지능형 컴플라이언스 →</Link>
      </Links>

      <TitleTemplate
        title="Contact Point"
        noImage
        subHeader="언제나 믿고 맡길 수 있는 최고의 멤버와 함께합니다."
      />
      <ContactPoints members={["cupark"]} />
    </PageTemplate>
  );
};
export default SupplyChainPage;

const Issues = styled.div`
  display: flex;
  flex-direction: ${({ breakpoints }) => (breakpoints.md ? "row" : "column")};
  gap: 1rem;
  & > div {
    ${softCard}
    position: relative;
    overflow: hidden;
    flex: 1;
    padding: 1.75rem 1.5rem 1.5rem;
    &:before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, ${({ theme }) => theme.colors.accent}, ${({ theme }) => theme.colors.dark});
    }
    & > b {
      font-size: 1.2rem;
      color: ${({ theme }) => theme.colors.dark};
    }
    & > p {
      margin: 0.6em 0 0 0;
      line-height: 1.75;
      color: ${({ theme }) => theme.colors.monotone.dark};
    }
  }
`;
const Process = styled.div`
  display: grid;
  grid-template-columns: ${({ breakpoints }) => (breakpoints.laptop ? "repeat(4, minmax(0, 1fr))" : breakpoints.md ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)")};
  gap: 1rem;
  & > div {
    ${card}
    padding: 1.5rem;
  }
  & li {
    line-height: 1.7;
  }
`;
const Links = styled.div`
  ${linkPills}
  padding-top: 2rem;
`;
